/**
 * Email/Password Authentication Provider
 * Handles authentication via email and password with JWT tokens
 */

import type { AuthProvider, UserContext, EmailCredentials, SignInResponse } from '../types';
import { BaseAuthProvider } from './BaseAuthProvider';
import { apiFetch } from '../../ultra-meet-config';
import { buildDisplayName } from '../../runtime-config';

export class EmailAuthProvider extends BaseAuthProvider implements AuthProvider {
    readonly name = 'email' as const;

    async isAuthenticated(): Promise<boolean> {
        const user = this.getStoredUser();
        return user !== null;
    }

    async getCurrentUser(): Promise<UserContext | null> {
        return this.getStoredUser();
    }

    async signIn(credentials?: unknown): Promise<void> {
        if (!credentials || typeof credentials !== 'object') {
            throw new Error('Email credentials required');
        }

        const { email, password } = credentials as EmailCredentials;

        if (!email || !password) {
            throw new Error('Email and password are required');
        }

        const response = await apiFetch('/api/v1/auth/sign-in', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email, password })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(errorData.error || `Sign in failed: ${response.status}`);
        }

        const data: SignInResponse = await response.json();

        if (data.result !== 'success') {
            throw new Error('Sign in failed');
        }

        this.storeAuthData(data);
    }

    async signOut(): Promise<void> {
        this.clearStorage();
    }

    async refreshTokens(): Promise<void> {
        const refreshToken = this.getStoredRefreshToken();
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        const response = await apiFetch('/api/v1/auth/refresh', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${refreshToken}`
            }
        });

        if (!response.ok) {
            throw new Error(`Token refresh failed: ${response.status}`);
        }

        const data = await response.json();

        if (data.result !== 'success') {
            throw new Error('Token refresh failed');
        }

        // Store new tokens
        this.storeTokens(data.token, data.refresh_token);
    }

    async getAccessToken(): Promise<string | null> {
        return this.getStoredToken();
    }

    private storeAuthData(data: SignInResponse): void {
        const user: UserContext = {
            id: data.user_uuid,
            email: data.email,
            firstName: data.first_name,
            lastName: data.last_name,
            displayName: buildDisplayName(data.first_name, data.last_name, data.email),
            photoUrl: data.photo_url || ''
        };

        this.storeUser(user);
        this.storeTokens(data.token, data.refresh_token);
    }
}
